import React from 'react';
import { Link } from 'react-router-dom';
import {
  Zap,
  Globe,
  Palette,
  Code,
  Check,
  ArrowRight,
  Package,
  Shield,
  Smartphone,
  Database,
  Layers,
} from 'lucide-react';

const HomePage: React.FC = () => {
  const features = [
    {
      icon: Palette,
      title: 'Headless Architecture',
      description: 'No UI components - bring your own styling and design system.',
    },
    {
      icon: Globe,
      title: 'Country-Specific Validation',
      description: 'Dynamic schemas that adapt to different countries and regions.',
    },
    {
      icon: Code,
      title: 'Framework Agnostic',
      description: 'React hooks, Vue composables, and vanilla JavaScript support.',
    },
    {
      icon: Shield,
      title: 'TypeScript First',
      description: 'Full TypeScript support with comprehensive type definitions.',
    },
    {
      icon: Zap,
      title: 'Performance Optimized',
      description: 'Lightweight, tree-shakeable, and optimized for production.',
    },
    {
      icon: Smartphone,
      title: 'Responsive Ready',
      description: 'Works seamlessly with any CSS framework or design system.',
    },
    {
      icon: Globe,
      title: 'Internationalization',
      description: 'Multi-language support with RTL handling built-in.',
    },
    {
      icon: Database,
      title: 'Storage Agnostic',
      description: 'Pluggable storage adapters for memory, localStorage, or APIs.',
    },
  ];

  const frameworks = [
    { name: 'React', color: 'from-blue-400 to-blue-600' },
    { name: 'Vue 3', color: 'from-green-400 to-green-600' },
    { name: 'Vue 2', color: 'from-green-500 to-green-700' },
    { name: 'Next.js', color: 'from-gray-700 to-gray-900' },
    { name: 'Vanilla JS', color: 'from-yellow-400 to-yellow-600' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="section bg-gradient-to-br from-salla-primary/10 via-salla-secondary/10 to-primary-100">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-salla-primary mb-8 shadow-sm">
              <Package className="w-4 h-4" />
              <span>v3.0.0 - Now with enhanced TypeScript support</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Headless Address Management
              <span className="block text-salla-primary mt-2">
                For Modern Web Apps
              </span>
            </h1>

            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              A powerful, framework-agnostic library for building custom address forms
              with built-in validation, country-specific schemas, and seamless
              integration with React, Vue, or vanilla JavaScript.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/components" className="btn btn-primary text-lg px-8 py-3 flex items-center space-x-2">
                <Layers className="w-5 h-5" />
                <span>View Components</span>
              </Link>
              <Link to="/docs" className="btn btn-secondary text-lg px-8 py-3 flex items-center space-x-2">
                <span>Get Started</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/examples" className="btn btn-outline text-lg px-8 py-3 flex items-center space-x-2">
                <Code className="w-5 h-5" />
                <span>Examples</span>
              </Link>
            </div>

            {/* NPM Install Command */}
            <div className="mt-12 max-w-2xl mx-auto">
              <div className="bg-gray-900 rounded-lg p-4 text-left shadow-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-400 font-mono">TERMINAL</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText('npm install salla-address-manager');
                    }}
                    className="text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    Copy
                  </button>
                </div>
                <code className="text-green-400 font-mono text-sm">
                  npm install salla-address-manager
                </code>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Components Showcase */}
      <section className="section bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-blue-600 mb-6 shadow-sm">
              <Layers className="w-4 h-4" />
              <span>UI Components Available</span>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ready-to-Use Components
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Pre-built React components that work out of the box, or use the headless API
              for complete control. Built on the same powerful foundation.
            </p>
            <Link 
              to="/components" 
              className="inline-flex items-center space-x-2 btn btn-primary text-lg px-8 py-3"
            >
              <Layers className="w-5 h-5" />
              <span>Explore Components</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12">
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Compound Components</h3>
              <p className="text-gray-600 text-sm">
                Flexible composition with AddressForm.Root, Field, and convenience components
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Palette className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">Fully Customizable</h3>
              <p className="text-gray-600 text-sm">
                Complete control over styling, rendering, and behavior with render props
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md border border-gray-200">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="font-bold text-lg mb-2">TypeScript First</h3>
              <p className="text-gray-600 text-sm">
                Full type safety with comprehensive interfaces and IntelliSense support
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Framework Support */}
      <section className="section bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-center mb-4">
            Works With Your Favorite Framework
          </h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            First-class support for React, Vue, and vanilla JavaScript with
            framework-specific adapters and hooks.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            {frameworks.map((framework) => (
              <div
                key={framework.name}
                className={`px-6 py-3 rounded-lg bg-gradient-to-r ${framework.color} text-white font-semibold shadow-lg transform hover:scale-105 transition-transform`}
              >
                {framework.name}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section bg-gray-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Everything You Need</h2>
            <p className="text-xl text-gray-600">
              Built with developer experience in mind, packed with features that
              make address management a breeze.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="card hover:shadow-md transition-shadow group"
              >
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-salla-primary to-salla-secondary flex items-center justify-center group-hover:scale-110 transition-transform">
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
                <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code Example */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Simple & Powerful API</h2>
              <p className="text-xl text-gray-600">
                Get started in minutes with our intuitive API
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* React Example */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">React</h3>
                  <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    TypeScript
                  </span>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{`import { useAddressForm } from 'salla-address-manager/react';

function AddressForm() {
  const form = useAddressForm({
    locale: 'ar',
    validation: {
      validateOnChange: true
    }
  });

  return (
    <form onSubmit={form.handleSubmit}>
      <input {...form.getFieldProps('street')} />
      {form.errors.street && (
        <span>{form.errors.street}</span>
      )}
    </form>
  );
}`}</code>
                </pre>
              </div>

              {/* Vanilla JS Example */}
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-lg">Vanilla JavaScript</h3>
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                    ES6+
                  </span>
                </div>
                <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{`import { createAddressSystem } from 'salla-address-manager';

const system = createAddressSystem({
  locale: 'ar',
  validation: {
    validateOnChange: true
  }
});

const form = system.createFormController();

form.setFieldValue('country', 'SA');
form.setFieldValue('city', 'riyadh');

const success = await form.submit();`}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="section bg-gradient-to-br from-salla-primary to-salla-secondary text-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">Why Choose Salla Address Manager?</h2>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                'Zero dependencies on UI libraries',
                'Comprehensive TypeScript support',
                'Built-in form state management',
                'Automatic field clearing logic',
                'Country-specific validation rules',
                'Async data fetching support',
                'Multi-language i18n support',
                'Production-ready and battle-tested',
              ].map((benefit) => (
                <div key={benefit} className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center mt-1">
                    <Check className="w-4 h-4" />
                  </div>
                  <span className="text-lg">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Join developers building better address forms with our powerful library.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/components" className="btn btn-primary text-lg px-8 py-3">
                Try Components
              </Link>
              <Link to="/docs" className="btn btn-secondary text-lg px-8 py-3">
                Read Documentation
              </Link>
              <Link to="/examples" className="btn btn-outline text-lg px-8 py-3">
                Explore Examples
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;

